import DashboardControl from '@/components/dashboard/DashboardControl'
import OrderWidget, {
  type OrderWidgetHandle,
  type OrderSummary,
} from '@/components/order/OrderWidget'
import NotificationsPanel from '../notifications/NotificationsPanel'
import {
  Box,
  Button,
  Card,
  Flex,
  HoverCard,
  IconButton,
  Menu,
  Portal,
  Status,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  LuBell,
  LuCircleHelp,
  LuLayoutDashboard,
  LuLogOut,
  LuPaperclip,
  LuSettings,
  LuShieldQuestion,
  LuUser,
} from 'react-icons/lu'
import { IoMdClose } from 'react-icons/io'
import ActivityCalendar, {
  type ActivityCalendarHandle,
} from '../../components/activity/ActivityCalendar'
import { useUserStore } from '@/stores/user.store'
import { useLocation, useNavigate } from 'react-router-dom'
import { logoutApi } from '@/api/auth.api'
import { SUPPORT_PORTAL_URL } from '@/config'
import { useOnboardingStore } from '@/stores/onboarding.store'
import {
  getBankById,
  getInvoiceSettings,
  getOrderCapacities,
  getProfileSettings,
} from '@/api/settings.api'
import type { BankService, OrderSettings } from '@/common/settings.common'
import { hasCompletedCapacitySettings, hasCompletedUnionSettings } from '@/helper/order.helper'
import {
  getNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  type NotificationDTO,
} from '@/api/notifications.api'
import InvoiceWidget, { type InvoiceWidgetHandle } from '@/components/invoices/InvoiceWidget'
import type { InvoiceSettingsData } from '@/api/dto/settings.dto'
import { validateMandatoryInvoiceSettings } from '@/helper/invoice.helpers'
import { MobileSidebar } from '@/components/navigation/MobileSidebar'
import { DesktopSidebar } from '@/components/navigation/DesktopSidebar'
import TimeTracking from '@/pages/timetracking/TimeTracking'
import type { CheckboxCardOption } from '@/ui/components/CheckboxCardFrame'
import { InvoiceSettingsMigrationDialog } from '@/components/modals/InvoiceSettingsMigrationDialog'
import { getUserDisplayName } from '@/helper/common.helper'
import { LuStore } from 'react-icons/lu'
import { useUnseenMarketplaceDemands } from '@/features/marketplace/hooks/useUnseenMarketplaceDemands'
import { UnseenIndicatorDot } from '@/components/common/UnseenIndicatorDot'
import { SibHeader, SIB_HEADER_HEIGHT } from '@/components/layout/SibHeader'
import { PageContainer } from '@/components/layout/PageContainer'

export type DashboardItems = 'activities' | 'invoices' | 'orders' | 'workload' | 'notifications'

const DASHBOARD_ARRANGE_KEY = 'dashboard.arrange.hidden'

type UiNotification = {
  id: string
  title: string
  message: string
  dateLabel: string
  isRead: boolean
  orderId?: number
  createdAt?: string
}

export default function UserDashboard({
  children,
}: {
  children?: React.ReactNode
}): React.ReactElement {
  const { setUser, user, profile, setProfile, sessionRestored } = useUserStore()
  const navigate = useNavigate()
  const onboarding = useOnboardingStore()
  const profileDisplayName = getUserDisplayName(user, profile)
  const DASHBOARD_LAYOUT_KEY = `dashboard.layout.v1.${user?.id ?? 'anonymous'}`
  const [showInvoiceMigration, setShowInvoiceMigration] = useState(false)
  // const [widgets, setWidgets] = useState<DashboardItems[]>([
  //   'activities',
  //   'invoices',
  //   'orders',
  //   'workload',
  //   'notifications',
  // ])

  type DashboardLayoutItem = {
    value: DashboardItems
    visible: boolean
  }

  const DEFAULT_LAYOUT: DashboardLayoutItem[] = [
    { value: 'activities', visible: true },
    { value: 'invoices', visible: true },
    { value: 'orders', visible: true },
    { value: 'workload', visible: true },
    { value: 'notifications', visible: true },
  ]

  const [layout, setLayout] = useState<DashboardLayoutItem[]>(() => {
    if (typeof window === 'undefined') return DEFAULT_LAYOUT
    try {
      const raw = localStorage.getItem(DASHBOARD_LAYOUT_KEY)
      if (!raw) return DEFAULT_LAYOUT
      const parsed = JSON.parse(raw) as DashboardLayoutItem[]
      if (!Array.isArray(parsed)) return DEFAULT_LAYOUT

      const allowed = new Set<DashboardItems>([
        'activities',
        'invoices',
        'orders',
        'workload',
        'notifications',
      ])

      const cleaned = parsed.filter(x => allowed.has(x.value))
      const withMissing = [
        ...cleaned,
        ...DEFAULT_LAYOUT.filter(d => !cleaned.some(x => x.value === d.value)),
      ]
      return withMissing
    } catch {
      return DEFAULT_LAYOUT
    }
  })
  useEffect(() => {
    let alive = true

    ;(async () => {
      try {
        const data = await getProfileSettings()
        if (!alive) return
        setProfile(data)
      } catch (e) {
        console.error('Failed to load profile settings:', e)
      }
    })()

    return () => {
      alive = false
    }
  }, [setProfile])

  useEffect(() => {
    localStorage.setItem(DASHBOARD_LAYOUT_KEY, JSON.stringify(layout))
  }, [layout])

  const widgets = useMemo(() => layout.filter(x => x.visible).map(x => x.value), [layout])
  const calendarRef = useRef<ActivityCalendarHandle>(null)
  const orderWidgetRef = useRef<OrderWidgetHandle>(null)
  const invoiceWidgetRef = useRef<InvoiceWidgetHandle>(null)

  const [capacities, setCapacities] = useState<OrderSettings[]>([])
  const [unionBankServices, setUnionBankServices] = useState<BankService[]>([])
  const [invoiceSettings, setInvoiceSettings] = useState<InvoiceSettingsData | null>(null)
  const [ordersFromWidget, setOrdersFromWidget] = useState<OrderSummary[]>([])

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [isMobileSidebarOpen, setMobileSidebarOpen] = useState(false)

  // Marketplace navigation indicator (SIB-855). Polls while the dashboard is mounted; the
  // count clears once the SIB opens a demand and returns here.
  const { hasUnseen: hasUnseenDemands, count: unseenDemandCount } = useUnseenMarketplaceDemands()

  const [sidebarNotifications, setSidebarNotifications] = useState<UiNotification[]>([])
  const [sidebarNotificationsLoading, setSidebarNotificationsLoading] = useState(false)
  const [focusNotificationId, setFocusNotificationId] = useState<string | null>(null)
  const [notificationsRefreshKey, setNotificationsRefreshKey] = useState(0)

  const [recipients, setRecipients] = useState<CheckboxCardOption[]>([])

  const plan = useMemo(() => ({ TOJ: 0, Beratung: 0, Schulung: 0, Pilot: 0 }), [])

  const serviceTabs = useMemo(() => ['Übersicht', 'TOJ', 'Beratung', 'Schulung', 'Pilot'], [])

  const HEADER_H = SIB_HEADER_HEIGHT
  const SIDEBAR_W = 270 // px
  const PADDING_PX = 16 // p={4}
  // const sidebarW = sidebarCollapsed ? 0 : SIDEBAR_W

  // "Dashboard anordnen" dismiss persist
  const [showDashboardArrange, setShowDashboardArrange] = useState(() => {
    if (typeof window === 'undefined') return true
    return localStorage.getItem(DASHBOARD_ARRANGE_KEY) !== 'true'
  })

  const hideDashboardArrange = useCallback(() => {
    setShowDashboardArrange(false)
    localStorage.setItem(DASHBOARD_ARRANGE_KEY, 'true')
  }, [])

  const resetDashboardArrange = useCallback(() => {
    setShowDashboardArrange(true)
    localStorage.removeItem(DASHBOARD_ARRANGE_KEY)
  }, [])

  const autoCollapse = useBreakpointValue({
    base: true,
    md: true,
    lg: false,
  })

  useEffect(() => {
    async function loadSettings() {
      try {
        const capRes = await getOrderCapacities()
        setCapacities(capRes.settings ?? [])
        const invRes = await getInvoiceSettings()
        setInvoiceSettings(invRes)
        const bankRes = await getBankById(1) // Union Investment
        setUnionBankServices(bankRes.bankServices ?? [])
        setRecipients(
          (bankRes.associatedEntities ?? []).map((e: any) => ({
            id: String(e.id),
            label: String(e.name ?? e.title ?? `Kunde ${e.id}`),
          }))
        )
      } catch (e) {
        console.error('Failed to load settings:', e)
      }
    }

    loadSettings()
  }, [])

  useEffect(() => {
    if (autoCollapse === undefined) return
    setSidebarCollapsed(autoCollapse)
  }, [autoCollapse])

  useEffect(() => {
    if (!sessionRestored) return
    if (!user) return

    const done = localStorage.getItem(MIGRATION_DONE_KEY) === '1'
    if (done) return

    const dismissedThisSession = sessionStorage.getItem(MIGRATION_SESSION_DISMISS_KEY) === '1'
    if (dismissedThisSession) return

    // Show once per login until user completes migration
    setShowInvoiceMigration(true)
  }, [sessionRestored, user])

  const missingCapacity = !hasCompletedCapacitySettings(capacities)
  const missingPrices = !hasCompletedUnionSettings(unionBankServices)
  const missingInvoiceSettings = !validateMandatoryInvoiceSettings(invoiceSettings).complete

  const MIGRATION_VERSION = 'v1'
  const MIGRATION_DONE_KEY = `sib_invoice_settings_migration_done_${MIGRATION_VERSION}`
  const MIGRATION_SESSION_DISMISS_KEY = `sib_invoice_settings_migration_dismissed_${MIGRATION_VERSION}`
  // Final condition
  const showSettingsWarning = missingCapacity || missingPrices || missingInvoiceSettings

  const orderById = useMemo(() => {
    return Object.fromEntries(ordersFromWidget.map(o => [Number(o.id), o])) as Record<
      number,
      OrderSummary
    >
  }, [ordersFromWidget])

  const sidebarAttachmentsCountFor = useCallback(
    (n: UiNotification) => {
      if (!n.orderId) return 0
      return orderById[n.orderId]?.attachmentCount ?? 0
    },
    [orderById]
  )

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(v => !v)
    setTimeout(() => {
      calendarRef.current?.rerender()
    }, 500)
  }, [])

  const toggleMobileSidebar = useCallback(() => {
    setMobileSidebarOpen(v => !v)
  }, [])

  const logout = useCallback(async () => {
    try {
      await logoutApi()
    } catch (e) {
      console.warn('Logout safely ignored:', e)
    } finally {
      setUser(null)
      navigate('/login', { replace: true })
    }
  }, [navigate, setUser])
  const location = useLocation()

  const formatDate = useCallback((iso?: string) => {
    if (!iso) return ''
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(d)
  }, [])

  const toSidebarUiNotification = useCallback(
    (dto: NotificationDTO): UiNotification => {
      return {
        id: String(dto.id),
        title: dto.title ?? 'Benachrichtigung',
        message: dto.message ?? '',
        dateLabel: formatDate(dto.createdAt),
        createdAt: dto.createdAt,
        isRead: dto.read ?? !!dto.readAt,
        orderId: (dto as any).orderId,
      }
    },
    [formatDate]
  )

  useEffect(() => {
    let alive = true

    ;(async () => {
      try {
        setSidebarNotificationsLoading(true)
        const res = await getNotifications({ status: 'all', page: 0, pageSize: 20 })
        if (!alive) return
        setSidebarNotifications(res.map(toSidebarUiNotification))
      } catch (e) {
        console.error('Failed to load sidebar notifications:', e)
      } finally {
        if (alive) setSidebarNotificationsLoading(false)
      }
    })()

    return () => {
      alive = false
    }
  }, [toSidebarUiNotification])

  useEffect(() => {
    const action = (location.state as any)?.action
    if (!action) return

    if (action === 'new-order') {
      orderWidgetRef.current?.openCreateOrder?.()
    }

    if (action === 'create-invoice') {
      invoiceWidgetRef.current?.openCreateInvoice?.()
    }

    // clear state so refresh/back doesn't reopen
    navigate(location.pathname, { replace: true, state: {} })
  }, [location.state, location.pathname, navigate])

  const ensureNotificationsWidgetVisibleAndScroll = useCallback(() => {
    setLayout(prev => {
      const i = prev.findIndex(x => x.value === 'notifications')
      if (i === -1) return prev

      const next = [...prev]
      const [notif] = next.splice(i, 1)
      next.unshift({ ...notif, visible: true })
      return next
    })

    setTimeout(() => {
      document
        .getElementById('dashboard-notifications')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 0)
  }, [])

  const markAllNotificationsRead = useCallback(async () => {
    // optimistic UI update
    setSidebarNotifications(prev => prev.map(n => ({ ...n, isRead: true })))

    try {
      await markAllNotificationsAsRead()
      setNotificationsRefreshKey(k => k + 1)
    } catch (e) {
      console.error('Failed to mark all notifications as read', e)
    }
  }, [])

  const unreadCount = sidebarNotifications.filter(n => !n.isRead).length

  const sidebarPreviewNotifications = useMemo(() => {
    return sidebarNotifications
      .filter(n => !n.isRead)
      .sort((a, b) => {
        const at = a.createdAt ? Date.parse(a.createdAt) : 0
        const bt = b.createdAt ? Date.parse(b.createdAt) : 0
        return bt - at
      })
    // .slice(0, 3)
  }, [sidebarNotifications])

  const getSettingsTarget = useCallback(() => {
    // If BANK SERVICES (prices) are missing → go to Auftraggeber
    if (missingPrices) return '/settings/bank'

    // If CAPACITY is missing → go to Bestellungen
    if (missingCapacity) return '/settings/orders'

    // If INVOICE SETTINGS are missing → go to Rechnung
    if (missingInvoiceSettings) return '/settings/invoice'
    // Default behavior
    return '/settings/invoice'
  }, [missingCapacity, missingInvoiceSettings, missingPrices])

  const handleNewOrder = useCallback(() => {
    orderWidgetRef.current?.openCreateOrder?.()
  }, [])

  const handleAddActivity = useCallback(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    calendarRef.current?.openCreateActivity?.()
  }, [])

  const handleCreateInvoice = useCallback(() => {
    invoiceWidgetRef.current?.openCreateInvoice?.()
  }, [])

  const componentFactory = useCallback(
    (items: DashboardItems[]) => {
      const dashboardItems: Record<DashboardItems, React.ReactNode> = {
        activities: (
          <ActivityCalendar
            hasCapacitySettingsSetup={!missingCapacity}
            hasUnionSettingsSetup={!missingPrices}
            ref={calendarRef}
          />
        ),
        invoices: <InvoiceWidget ref={invoiceWidgetRef} />,
        orders: (
          <OrderWidget
            hasCompletedCapacitySetup={!missingCapacity}
            hasUnionSettingsSetup={!missingPrices}
            hasInvoiceSettingsSetup={!missingInvoiceSettings}
            ref={orderWidgetRef}
            onOrdersLoaded={setOrdersFromWidget}
          />
        ),
        // workload: '',
        workload: (
          <TimeTracking
            year={new Date().getFullYear()}
            registrationYear={new Date().getFullYear()}
            recipients={recipients}
            serviceTabs={serviceTabs}
            plan={plan}
            onYearChange={() => {}}
          />
        ),
        notifications: (
          <NotificationsPanel
            orderById={orderById}
            focusNotificationId={focusNotificationId}
            onFocusHandled={() => setFocusNotificationId(null)}
            refreshKey={notificationsRefreshKey}
          />
        ),
      }

      return items.map(item => <React.Fragment key={item}>{dashboardItems[item]}</React.Fragment>)
    },
    [
      handleCreateInvoice,
      missingCapacity,
      missingInvoiceSettings,
      missingPrices,
      notificationsRefreshKey,
      focusNotificationId,
      orderById,
    ]
  )

  return (
    <Flex direction="column" minH="100vh">
      <InvoiceSettingsMigrationDialog
        open={showInvoiceMigration}
        onLater={() => {
          setShowInvoiceMigration(false)
          // show again next login
          sessionStorage.setItem(MIGRATION_SESSION_DISMISS_KEY, '1')
        }}
        onReview={() => {
          // user is starting the migration flow -> never show again
          localStorage.setItem(MIGRATION_DONE_KEY, '1')
          sessionStorage.removeItem(MIGRATION_SESSION_DISMISS_KEY)
          setShowInvoiceMigration(false)
          navigate('/settings/invoice')
        }}
      />
      <SibHeader
        userName={profileDisplayName}
        sidebarCollapsed={sidebarCollapsed}
        onToggleSidebar={toggleSidebar}
        onOpenMobileSidebar={toggleMobileSidebar}
        onNewOrder={handleNewOrder}
        onAddActivity={handleAddActivity}
        onCreateInvoice={handleCreateInvoice}
        onGoDashboard={() => navigate('/app/user/dashboard')}
        onOpenSupport={() => window.open(SUPPORT_PORTAL_URL, '_blank')}
        onLogout={logout}
      />

      {/* BODY */}
      <Flex align="flex-start" position="relative" zIndex={2}>
        {/* DESKTOP SIDEBAR */}
        <DesktopSidebar
          collapsed={sidebarCollapsed}
          widthPx={SIDEBAR_W}
          headerHeightPx={HEADER_H}
          pagePaddingPx={PADDING_PX}
        >
          <Flex h="full" direction="column" justify="space-between" gap={4}>
            <Flex direction="column" gap={4}>
              {/* Nav Top Menu */}
              <Flex data-testid="navigation-sidebar" direction="column" gap={4}>
                <Flex
                  direction={'column'}
                  alignItems={'flex-start'}
                  opacity={sidebarCollapsed ? 0 : 1}
                  visibility={sidebarCollapsed ? 'hidden' : 'visible'}
                  transition="opacity 0.2s ease, visibility 0.2s ease"
                  transitionDelay={sidebarCollapsed ? '0s' : '0.15s'}
                >
                  <Button
                    colorPalette={'gray'}
                    variant={'ghost'}
                    width="full"
                    justifyContent="flex-start"
                  >
                    <LuLayoutDashboard />
                    Dashboard
                  </Button>

                  <Button
                    colorPalette="gray"
                    variant="ghost"
                    width="full"
                    justifyContent="flex-start"
                    onClick={() => navigate('/app/user/marketplace')}
                    aria-label={
                      hasUnseenDemands
                        ? `Marktplatz, ${unseenDemandCount} neue Ausschreibungen`
                        : 'Marktplatz'
                    }
                    title={
                      hasUnseenDemands
                        ? `${unseenDemandCount} neue Ausschreibungen im Marktplatz`
                        : undefined
                    }
                  >
                    <UnseenIndicatorDot
                      show={hasUnseenDemands}
                      data-testid="marketplace-unseen-indicator"
                    >
                      <LuStore />
                    </UnseenIndicatorDot>
                    <Text>Marktplatz</Text>
                  </Button>

                  <Menu.Root positioning={{ placement: 'right-start', gutter: 10 }}>
                    <Menu.Trigger asChild>
                      <Button
                        aria-label={`Benachrichtigungen, ${unreadCount} ungelesen`}
                        variant="ghost"
                        width="full"
                        justifyContent="flex-start"
                        title={`${unreadCount} ungelesene Benachrichtigungen`}
                        disabled={sidebarNotificationsLoading}
                      >
                        {/* {unreadCount > 0 ? <LuBellDot /> : <LuBell />}
                         */}
                        <Box position="relative" display="inline-flex">
                          <LuBell size={18} />

                          {unreadCount > 0 && (
                            <Status.Root
                              position="absolute"
                              top="-2px"
                              right="-2px"
                              size="sm"
                              colorPalette="yellow"
                            >
                              <Status.Indicator />
                            </Status.Root>
                          )}
                        </Box>
                        Benachrichtigungen
                      </Button>
                    </Menu.Trigger>

                    <Portal>
                      <Menu.Positioner>
                        <Menu.Content width="320px" zIndex="popover">
                          <Menu.Item
                            value="mark-all-read"
                            disabled={unreadCount === 0}
                            onClick={() => {
                              if (unreadCount === 0) return
                              markAllNotificationsRead()
                              ensureNotificationsWidgetVisibleAndScroll()
                              setFocusNotificationId(null)
                            }}
                          >
                            Alle als gelesen markieren
                          </Menu.Item>

                          <Menu.Item
                            value="overview"
                            onClick={() => {
                              ensureNotificationsWidgetVisibleAndScroll()
                              setFocusNotificationId(null)
                            }}
                          >
                            Zur Übersicht
                          </Menu.Item>

                          <Menu.Separator />

                          {/* NOTIFICATION PREVIEWS */}
                          {sidebarPreviewNotifications.length === 0 ? (
                            <Menu.Item value="empty" disabled>
                              Keine ungelesenen Benachrichtigungen.
                            </Menu.Item>
                          ) : (
                            <Box maxH="280px" overflowY="auto" pr={1}>
                              {sidebarPreviewNotifications.map(n => (
                                <Menu.Item
                                  key={n.id}
                                  value={n.id}
                                  onClick={async () => {
                                    ensureNotificationsWidgetVisibleAndScroll()
                                    setFocusNotificationId(n.id)

                                    // optimistic update
                                    setSidebarNotifications(prev =>
                                      prev.map(x => (x.id === n.id ? { ...x, isRead: true } : x))
                                    )

                                    try {
                                      await markNotificationAsRead(Number(n.id))
                                    } catch (e) {
                                      console.error(
                                        'Failed to mark notification as read from sidebar:',
                                        e
                                      )
                                      // rollback (optional)
                                      setSidebarNotifications(prev =>
                                        prev.map(x =>
                                          x.id === n.id ? { ...x, isRead: false } : x
                                        )
                                      )
                                    }
                                  }}
                                  alignItems="flex-start"
                                >
                                  <Box width="100%">
                                    <Text>{n.title}</Text>
                                    <Text>{n.message}</Text>

                                    <Flex mt={1} justify="space-between" align="center">
                                      <Text>{n.dateLabel}</Text>
                                    </Flex>

                                    <Flex gap={1} align="center" color="fg.muted">
                                      <LuPaperclip />
                                      <Text>{sidebarAttachmentsCountFor(n)}</Text>
                                    </Flex>
                                  </Box>
                                </Menu.Item>
                              ))}
                            </Box>
                          )}
                        </Menu.Content>
                      </Menu.Positioner>
                    </Portal>
                  </Menu.Root>

                  <Button
                    data-testid="sidebar-profile"
                    colorPalette={'gray'}
                    variant={'ghost'}
                    width="full"
                    justifyContent="flex-start"
                    onClick={() => navigate('/settings/profile')}
                  >
                    <LuUser />
                    <Text>{profileDisplayName}</Text>
                    {/* <Text>{user?.displayName || ''}</Text> */}
                  </Button>

                  <Box position="relative" width="full">
                    {/* SETTINGS BUTTON */}
                    <Button
                      id="settings-navigation"
                      colorPalette="gray"
                      variant="ghost"
                      width="full"
                      justifyContent="flex-start"
                      onClick={() => navigate('/settings/')}
                    >
                      <LuSettings />
                      <Text>Einstellungen</Text>
                    </Button>

                    {/* WARNING DOT + POPOVER (instead of Tooltip) */}
                    {showSettingsWarning && (
                      <HoverCard.Root openDelay={200} closeDelay={300}>
                        <HoverCard.Trigger asChild>
                          <Box
                            position="absolute"
                            right="0"
                            top="0"
                            width="50px"
                            height="40px"
                            display="flex"
                            alignItems="center"
                            justifyContent="flex-end"
                            cursor="pointer"
                            zIndex={10}
                          >
                            <Status.Root size="sm" colorPalette="orange">
                              <Status.Indicator />
                            </Status.Root>
                          </Box>
                        </HoverCard.Trigger>

                        <HoverCard.Positioner zIndex={9999}>
                          <HoverCard.Content maxW="240px">
                            <HoverCard.Arrow />

                            <Box>
                              <Text>Einstellungen erforderlich</Text>

                              {missingCapacity && (
                                <Text>⚠️ Kapazitäten für Union Investment fehlen.</Text>
                              )}
                              {missingInvoiceSettings && (
                                <Text>⚠️ Rechnungseinstellungen fehlen.</Text>
                              )}
                              {missingPrices && <Text>⚠️ Preise pro Einheit fehlen.</Text>}
                              <Button onClick={() => navigate(getSettingsTarget())}>
                                Jetzt öffnen
                              </Button>
                            </Box>
                          </HoverCard.Content>
                        </HoverCard.Positioner>
                      </HoverCard.Root>
                    )}
                  </Box>

                  <Button
                    id="dashboard-root"
                    colorPalette={'gray'}
                    variant={'ghost'}
                    width="full"
                    justifyContent="flex-start"
                    onClick={() => {
                      resetDashboardArrange()
                      onboarding.resetWizard()
                      onboarding.startWizard()
                    }}
                  >
                    <LuShieldQuestion />
                    <Text>Einführung erneut starten</Text>
                  </Button>

                  <Button
                    colorPalette={'gray'}
                    variant={'ghost'}
                    width="full"
                    justifyContent="flex-start"
                    onClick={() => window.open(SUPPORT_PORTAL_URL, '_blank')}
                  >
                    <LuCircleHelp />
                    <Text>Support</Text>
                  </Button>

                  <Button
                    colorPalette={'gray'}
                    variant={'ghost'}
                    width="full"
                    justifyContent="flex-start"
                    onClick={logout}
                  >
                    <LuLogOut />
                    <Text>Abmelden</Text>
                  </Button>
                </Flex>
              </Flex>
            </Flex>

            {/* Nav Bottom Menu */}
            <Flex direction="column" gap={2}>
              {/* Dashboeard anordnen */}
              <Flex gap={2} direction={'column'}>
                {showDashboardArrange && (
                  <Card.Root
                    position="relative"
                    opacity={sidebarCollapsed ? 0 : 1}
                    visibility={sidebarCollapsed ? 'hidden' : 'visible'}
                    transition="opacity 0.2s ease, visibility 0.5s ease"
                    transitionDelay={sidebarCollapsed ? '0s' : '0.15s'}
                  >
                    <Card.Body>
                      <Box position="absolute" top={0} right={0}>
                        <IconButton
                          variant="ghost"
                          aria-label="Schließen"
                          onClick={hideDashboardArrange}
                        >
                          <IoMdClose />
                        </IconButton>
                      </Box>
                      <Card.Title>Dashboard anordnen</Card.Title>
                      <Card.Description>
                        Aktivieren Sie die Ansichten, die Sie gerne sehen möchten und ordnen Sie
                        diese per Drag-and-drop an.
                      </Card.Description>
                    </Card.Body>
                  </Card.Root>
                )}

                <Flex
                  width="full"
                  minW={0}
                  id="dashboard-widget-config"
                  opacity={sidebarCollapsed ? 0 : 1}
                  visibility={sidebarCollapsed ? 'hidden' : 'visible'}
                  transition="opacity 0.2s ease, visibility 0.2s ease"
                  transitionDelay={sidebarCollapsed ? '0s' : '0.15s'}
                >
                  <DashboardControl
                    items={layout.map(x => ({
                      value: x.value,
                      visible: x.visible,
                      disabled: false,
                      label:
                        x.value === 'activities'
                          ? 'Aktivitäten'
                          : x.value === 'invoices'
                            ? 'Rechnungen'
                            : x.value === 'orders'
                              ? 'Bestellungen'
                              : x.value === 'workload'
                                ? 'Arbeitszeit'
                                : 'Benachrichtigungen',
                    }))}
                    onChange={nextItems => {
                      setLayout(nextItems.map(i => ({ value: i.value, visible: i.visible })))
                    }}
                  />
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </DesktopSidebar>

        {/* RIGHT CONTENT */}
        <Flex flex={1} minW={0}>
          <PageContainer variant="dashboard">{componentFactory(widgets)}</PageContainer>
        </Flex>
      </Flex>

      {/* Mobile Sidebar */}
      <MobileSidebar
        isOpen={isMobileSidebarOpen}
        onClose={toggleMobileSidebar}
        userName={user?.displayName || ''}
        onDashboard={() => navigate('/app/user/dashboard')}
        onMarketplace={() => navigate('/app/user/marketplace')}
        unseenMarketplaceCount={unseenDemandCount}
        onSupport={() => window.open(SUPPORT_PORTAL_URL, '_blank')}
        onSettings={() => navigate('/settings/invoice')}
        onProfile={() => navigate('/settings/profile')}
        onLogout={logout}
        onRestartOnboarding={() => {
          onboarding.resetWizard()
          onboarding.startWizard()
        }}
      />
      <Box>{children}</Box>
    </Flex>
  )
}
